<template>
  <div>
    <!-- Banner: pending imaging requests (ST-006) -->
    <ol class="flex items-center w-full p-3 justify-around space-x-2 text-sm font-medium text-center text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm sm:text-base">
      <li v-for="(request, index) in filteredRequests" :key="request.id"
          class="flex items-center cursor-pointer"
          :class="{ 'text-custom-blue': selectedTest === request.id }"
          @click="selectTest(request.id)">
        <span class="flex items-center justify-center w-5 h-5 me-2 text-xs border rounded-full shrink-0"
              :class="selectedTest === request.id ? 'border-custom-blue' : 'border-gray-500'">
          {{ index + 1 }}
        </span>
        {{ request.imaging_type.name }}
      </li>
    </ol>

    <div v-if="filteredRequests.length === 0" class="text-center text-green-600 font-medium mt-6">
      All imaging tests have been processed or are in review!
    </div>

    <div v-if="selectedTest && currentRequest.status?.code === 'ST-006'" class="grid grid-cols-1 gap-4 h-[55vh] overflow-y-auto mt-3">

      <!-- Template Selector (full width, above main grid) -->
      <div v-if="filteredTemplates.length" class="flex flex-wrap items-center gap-3 px-8 pt-3 pb-3 border-b border-gray-100">
        <label class="font-semibold text-sm shrink-0">Quick Template:</label>
        <select v-model="activeTemplateId[selectedTest]"
                class="flex-1 min-w-[200px] border border-gray-300 rounded p-2 text-sm bg-gray-50">
          <option :value="null">— select a template —</option>
          <option v-for="t in filteredTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
        </select>
        <button type="button" @click="applyTemplate(selectedTest)"
                :disabled="!activeTemplateId[selectedTest]"
                class="px-3 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-40">
          Apply
        </button>
        <button v-if="sectionInputs[selectedTest]" type="button" @click="clearTemplate(selectedTest)"
                class="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 text-gray-600">
          Clear
        </button>
      </div>

      <div class="grid grid-cols-2 gap-6 px-8">
        <div class="mb-4">
          <h3 class="font-medium">Imaging Type:</h3>
          <p class="text-sm">{{ currentRequest.imaging_type?.name }}</p>
        </div>
        <div class="mb-4">
          <h3 class="font-medium">Body Part/Region:</h3>
          <p class="text-sm">{{ currentRequest.body_part || 'Not specified' }}</p>
        </div>
        <div class="mb-4">
          <h3 class="font-medium">Date Requested:</h3>
          <p class="text-sm">{{ formatDate(currentRequest.created_at) }}</p>
        </div>

        <!-- Free-text Findings (when no template active) -->
        <div v-if="!sectionInputs[selectedTest]" class="flex flex-col">
          <label class="font-medium">Findings:</label>
          <textarea v-model="labForms[selectedTest].findings" rows="4"
                    placeholder="Describe findings..."
                    class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2" required />
        </div>

        <!-- Recommendations -->
        <div class="flex flex-col">
          <label class="font-medium">Recommendations:</label>
          <textarea v-model="labForms[selectedTest].recommendations" rows="4"
                    placeholder="Add recommendations..."
                    class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2" />
        </div>

        <!-- Upload Reports -->
        <div class="flex flex-col">
          <label class="font-medium">
            Upload Radiology Reports (Max 5 files, 10MB each)<br />
            <span class="text-xs text-gray-600">JPG, PNG, PDF</span>
          </label>
          <label for="fileInput"
                 class="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-500 rounded bg-gray-50 mt-2 cursor-pointer hover:bg-gray-100 transition"
                 @click="logButtonClick">
            <input id="fileInput" type="file" ref="fileInput" class="hidden"
                   @change="handleFileSelect" accept=".jpg,.jpeg,.png,.pdf" multiple />
            <img v-if="selectedFiles.length === 0" :src="uploadFile" alt="upload" class="w-6 h-6" />
            <span v-if="selectedFiles.length === 0" class="text-sm text-blue-600">Add Reports</span>
            <span v-else class="text-sm text-blue-600">{{ selectedFiles.length }} file(s) selected</span>
          </label>
          <div v-if="selectedFiles.length > 0" class="mt-3 flex flex-wrap gap-3">
            <div v-for="(file, index) in selectedFiles" :key="index" class="flex flex-col items-center gap-1 p-2 bg-gray-50 rounded max-w-[100px]">
              <span class="text-xs text-gray-600 text-center truncate w-full" :title="file.file.name">{{ file.file.name }}</span>

              <div v-if="file.file.type.startsWith('image/')" class="w-20 h-20">
                <img :src="file.preview" alt="preview" class="w-full h-full object-cover rounded" />
              </div>
              <div v-else-if="file.file.type === 'application/pdf'"
                   class="w-20 h-20 bg-red-50 border-2 border-red-200 rounded flex items-center justify-center">
                <span class="text-xs font-bold text-red-600">PDF</span>
              </div>

              <div v-if="uploadProgress[index]" class="w-full">
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div class="bg-custom-yellow h-2 rounded-full transition-all" :style="{ width: uploadProgress[index] + '%' }"></div>
                </div>
              </div>
              <button @click="removeFile(index)" class="text-red-500 hover:text-red-700">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div class="flex flex-col">
          <label class="font-medium">Notes:</label>
          <textarea v-model="labForms[selectedTest].comment" rows="3"
                    placeholder="Optional notes..."
                    class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-2" />
        </div>
      </div>

      <!-- Template Sections (full width, below main grid) -->
      <div v-if="sectionInputs[selectedTest]" class="px-8 pb-2">
        <h4 class="font-semibold text-sm mb-3 text-gray-700 border-b pb-1">Template Findings</h4>
        <div class="flex flex-col gap-4">
          <div v-for="(section, idx) in sectionInputs[selectedTest]" :key="idx" class="flex flex-col">
            <label class="font-medium text-sm">{{ section.title }}:</label>
            <textarea v-model="section.content" rows="3"
                      :placeholder="section.placeholder || ''"
                      class="w-full border-gray-300 rounded p-2 bg-gray-50 mt-1 text-sm" />
          </div>
        </div>
      </div>

      <div class="mt-4 px-8 flex justify-end pb-4">
        <button :disabled="showLoader || uploadInProgress" @click="handleTestSubmit"
                class="px-6 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600 disabled:opacity-50">
          {{ showLoader ? 'Submitting...' : 'Submit Test' }}
        </button>
      </div>
    </div>

    <!-- Submitted Test Results Table -->
    <div class="mt-8 px-8">
      <h2 class="text-xl font-bold mb-4">Submitted Test Results</h2>
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left font-medium text-gray-600">Imaging Type</th>
            <th class="px-6 py-3 text-left font-medium text-gray-600">Reports</th>
            <th class="px-6 py-3 text-left font-medium text-gray-600">Filled By</th>
            <th class="px-6 py-3 text-left font-medium text-gray-600">Findings</th>
            <th class="px-6 py-3 text-left font-medium text-gray-600">Recommendations</th>
            <th class="px-6 py-3 text-left font-medium text-gray-600">Notes</th>
            <th class="px-6 py-3 text-left font-medium text-gray-600">Status</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="(test, index) in submittedTests" :key="index">
            <td class="px-6 py-4 whitespace-nowrap">{{ test.imaging_type }}</td>
            <td class="px-6 py-4">
              <div v-for="(file, i) in test.url" :key="i" class="mb-1">
                <span class="text-blue-600 cursor-pointer hover:underline text-sm" @click="downloadFile(file.url)">
                  {{ file.filename || 'Download File' }}
                </span>
              </div>
              <span v-if="!test.url || test.url.length === 0" class="text-gray-500 text-sm">No Attachment</span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">{{ test.filled_by }}</td>
            <td class="px-6 py-4 text-sm text-gray-700 leading-relaxed">
              <div class="truncate max-w-xs">{{ test.findings || '—' }}</div>
            </td>
            <td class="px-6 py-4 text-sm text-gray-700 leading-relaxed">
              <div class="truncate max-w-xs">{{ test.recommendations || '—' }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">{{ test.comment || '—' }}</td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="px-3 py-1 text-xs font-semibold rounded-full"
                    :class="{
                      'bg-green-100 text-green-800': test.status === 'Published',
                      'bg-yellow-100 text-yellow-800': test.status === 'Completed',
                      'bg-blue-100 text-blue-800': test.status === 'Pending',
                      'bg-gray-100 text-gray-800': !test.status
                    }">
                {{ test.status || 'Draft' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import uploadFile from "@/assets/icons/upload.png";
import apiClient from "@/services/api";
import AWS from "aws-sdk";
import axios from "axios";

export default {
  props: {
    radiologyTest: { type: Object, required: true },
    requests: { type: Array, required: true }
  },
  data() {
    return {
      selectedTest: null,
      labForms: {},
      selectedFiles: [],
      uploadFile,
      showLoader: false,
      uploadProgress: [],
      templates: [],
      activeTemplateId: {},
      sectionInputs: {},
    };
  },
  computed: {
    filteredRequests() {
      const filtered = this.requests.filter(item => item.status?.code === 'ST-006');
      if (filtered.length && !this.selectedTest) {
        this.$nextTick(() => this.selectTest(filtered[0].id));
      }
      return filtered;
    },
    currentRequest() {
      return this.requests.find(r => r.id === this.selectedTest) || {};
    },
    uploadInProgress() {
      return this.uploadProgress.some(p => p > 0 && p < 100);
    },
    filteredTemplates() {
      const imagingTypeId = this.currentRequest?.imaging_type?.id;
      if (!imagingTypeId) return this.templates;
      return this.templates.filter(t => !t.imaging_type_id || t.imaging_type_id === imagingTypeId);
    },
    submittedTests() {
      return this.requests
        .filter(r => r.radiology_results?.length > 0)
        .map(r => ({
          id: r.id,
          imaging_type: r.imaging_type.name,
          filled_by: r.radiology_results[0].filled_by?.Username,
          findings: r.radiology_results[0].findings,
          recommendations: r.radiology_results[0].recommendation,
          url: r.radiology_results[0].url || [],
          comment: r.radiology_results[0].notes || '',
          status: r.status?.name || '',
        }));
    },
  },
  async created() {
    await this.loadTemplates();
  },
  watch: {
    requests: {
      handler(newVal) {
        if (newVal?.length && !this.selectedTest) {
          const pending = newVal.filter(r => r.status?.code === 'ST-006');
          if (pending.length) this.selectTest(pending[0].id);
        }
      },
      immediate: true,
    },
  },
  methods: {
    async loadTemplates() {
      try {
        const res = await apiClient.listRadiologyTemplates();
        if (res.success) this.templates = res.data;
      } catch (e) { /* silent */ }
    },
    selectTest(id) {
      this.selectedTest = id;
      if (!this.labForms[id]) {
        this.labForms[id] = { findings: '', recommendations: '', comment: '', url: [] };
      }
      if (!(id in this.activeTemplateId)) {
        this.activeTemplateId[id] = null;
      }
    },
    applyTemplate(testId) {
      const tid = this.activeTemplateId[testId];
      if (!tid) return;
      const tpl = this.templates.find(t => t.id === tid);
      if (!tpl?.sections?.length) return;
      this.sectionInputs[testId] = tpl.sections.map(s => ({ title: s.title, content: '', placeholder: s.placeholder_text || '' }));
    },
    clearTemplate(testId) {
      delete this.sectionInputs[testId];
      this.activeTemplateId[testId] = null;
    },
    buildFindings(testId) {
      const sections = this.sectionInputs[testId];
      if (!sections?.length) return this.labForms[testId]?.findings || '';
      return sections.map(s => `[${s.title.toUpperCase()}]\n${s.content}`).join('\n\n');
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      }).format(new Date(timestamp));
    },
    logButtonClick() {
      console.log("Add Radiology Reports clicked");
    },
    async handleTestSubmit() {
      const findings = this.buildFindings(this.selectedTest);
      if (!findings?.trim()) {
        this.$toast.error("Findings are required.");
        return;
      }

      this.showLoader = true;
      try {
        const uploadResults = await this.uploadLabFiles();
        const successfulUploads = uploadResults.filter(r => r.success);

        if (successfulUploads.length < uploadResults.length && this.selectedFiles.length > 0) {
          this.$toast.warning("Some files failed to upload");
        }

        this.labForms[this.selectedTest].url = successfulUploads.map(r => ({
          url: r.url, filename: r.filename
        }));

        const payload = {
          patient_visit_radiology_test_id: this.selectedTest,
          findings,
          recommendations: this.labForms[this.selectedTest].recommendations,
          comment: this.labForms[this.selectedTest].comment,
          url: this.labForms[this.selectedTest].url,
          userId: this.currentRequest.requested_by?.id,
          facilityId: this.currentRequest.facility_id
        };

        const response = await apiClient.submitRadiologyTestResult(payload);
        if (response.success) {
          this.$toast.success("Results saved successfully");

          const submittedId = this.selectedTest;
          this.$emit("refetch-requests");

          await this.$nextTick();
          await new Promise(resolve => this.$nextTick(resolve));

          const nextPending = this.requests.find(r =>
            r.status?.code === 'ST-006' && r.id !== submittedId
          );

          if (nextPending) {
            this.selectTest(nextPending.id);
          } else {
            this.selectedTest = null;
          }

          this.resetForm(submittedId);
        } else {
          this.$toast.error("Failed to save results");
        }
      } catch (err) {
        this.$toast.error("Network error");
        console.error(err);
      } finally {
        this.showLoader = false;
      }
    },
    async uploadLabFiles() {
      if (!this.selectedFiles.length) return [];
      this.uploadProgress = new Array(this.selectedFiles.length).fill(0);
      const results = await Promise.allSettled(
        this.selectedFiles.map(async (fileObj, index) => {
          const file = fileObj.file;
          const s3 = new AWS.S3({
            endpoint: process.env.VUE_APP_DO_ENDPOINT,
            signatureVersion: "v4",
            accessKeyId: process.env.VUE_APP_DO_ACCESS_KEY,
            secretAccessKey: process.env.VUE_APP_DO_SECRET_KEY,
            region: process.env.VUE_APP_DO_REGION
          });
          const timestamp = Date.now();
          const key = `radiology_result_${this.selectedTest}_${timestamp}_${file.name}`;
          const params = {
            Bucket: "jiji-bucket", Key: key, Body: file,
            ACL: "public-read", ContentType: file.type
          };
          try {
            const upload = s3.upload(params);
            upload.on("httpUploadProgress", (progress) => {
              const percentage = Math.round((progress.loaded / progress.total) * 100);
              this.uploadProgress[index] = percentage;
            });
            const result = await upload.promise();
            return { success: true, url: result.Location, filename: file.name };
          } catch (error) {
            console.error(`Upload failed for ${file.name}:`, error);
            return { success: false, url: "", filename: file.name, error: error.message };
          }
        })
      );
      return results.map(r => r.status === "fulfilled" ? r.value : r.reason);
    },
    handleFileSelect(event) {
      this.addFiles(Array.from(event.target.files));
    },
    addFiles(files) {
      const validFiles = files.filter(file =>
        ["image/jpeg", "image/png", "application/pdf"].includes(file.type) &&
        file.size <= 10 * 1024 * 1024
      );
      if (validFiles.length < files.length) {
        this.$toast.error("Some files were rejected: Only .jpg, .jpeg, .png, .pdf under 10MB allowed.");
      }
      const newFiles = validFiles.map(file => ({
        file, preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null
      }));
      this.selectedFiles = [...this.selectedFiles, ...newFiles].slice(0, 5);
    },
    removeFile(index) {
      const file = this.selectedFiles[index];
      if (file.preview) URL.revokeObjectURL(file.preview);
      this.selectedFiles.splice(index, 1);
      this.uploadProgress.splice(index, 1);
    },
    resetForm(submittedId) {
      this.selectedFiles.forEach(file => { if (file.preview) URL.revokeObjectURL(file.preview); });
      this.selectedFiles = [];
      this.uploadProgress = [];
      if (submittedId) {
        delete this.sectionInputs[submittedId];
        delete this.activeTemplateId[submittedId];
        if (this.labForms[submittedId]) {
          this.labForms[submittedId] = { findings: '', recommendations: '', comment: '', url: [] };
        }
      }
    },
    async downloadFile(fileUrl) {
      try {
        const response = await axios.get(fileUrl, { responseType: "blob" });
        let fileName = fileUrl.split("/").pop().split("?")[0];
        const cd = response.headers["content-disposition"];
        if (cd) {
          const match = cd.match(/filename="?([^"]+)"?/);
          if (match) fileName = match[1];
        }
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Download failed:", error);
        this.$toast.error("Failed to download file");
      }
    }
  }
};
</script>

<style scoped>
label, button {
  pointer-events: auto !important;
  cursor: pointer !important;
}
</style>
